import {
  commonAutoCompleteProps, commonInputProps, EditableDetailRow,
  Grid,
  MuiAutocomplete,
  MyTextInput
} from "@common";
import { getActivityNameById } from "@features";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useDispatch } from "react-redux";
import { getFieldErrorProps } from "./hooks/useActivityForm";

dayjs.extend(customParseFormat);

const parseDateTimeValue = (val) => {
  if (!val) return null;
  if (dayjs.isDayjs(val)) return val.isValid() ? val : null;
  const parsed = dayjs(val);
  return parsed.isValid() ? parsed : null;
};

const ActivityEdit = ({ formik, options, disabled }) => {
  const dispatch = useDispatch();
  const {
    activityTypeOptions = [],
    activityNameOptions = [],
    projectUsersOptions = [],
    activityStatusOptions = [],
    crmStatusOptions = [],
    crmPriorityOptions = [],
  } = options || {};

  const statusOptions =
    activityStatusOptions.length > 0 ? activityStatusOptions : crmStatusOptions;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Grid container spacing={1} sx={{ p: 1 }}>
        {/* LEFT COLUMN */}
        <Grid size={{ xs: 12, lg: 12 }}>
          <Grid container spacing={1}>
            {/* Activity Type */}
            <Grid size={12}>
              <EditableDetailRow label="Activity Type" required>
                <MuiAutocomplete
                  {...commonAutoCompleteProps}
                  name="activityType"
                  options={activityTypeOptions}
                  disabled={disabled}
                  getOptionLabel={(option) =>
                    typeof option === "string"
                      ? option
                      : option?.activityType || option?.name || ""
                  }
                  value={formik.values.activityType || null}
                  onChange={(e, val) => {
                    formik.setValues({
                      ...formik.values,
                      activityType: val,
                      activityName: null,
                    });
                    const typeId = val?.id || val?._id || val?.activityTypeId;
                    if (typeId) {
                      dispatch(getActivityNameById({ activityTypeId: typeId }));
                    }
                  }}
                  onBlur={formik.handleBlur}
                  {...getFieldErrorProps(formik, "activityType")}
                />
              </EditableDetailRow>
            </Grid>
            {/* Activity Name */}
            <Grid size={12}>
              <EditableDetailRow label="Activity Name" required>
                <MuiAutocomplete
                  key={
                    formik.values.activityType?.id ||
                    formik.values.activityType?.activityTypeId ||
                    formik.values.activityType?.name ||
                    "empty"
                  }
                  {...commonAutoCompleteProps}
                  name="activityName"
                  options={activityNameOptions}
                  disabled={disabled || !formik.values.activityType}
                  getOptionLabel={(option) =>
                    typeof option === "string"
                      ? option
                      : option?.activityName || option?.name || ""
                  }
                  value={formik.values.activityName || null}
                  onChange={(e, val) =>
                    formik.setFieldValue("activityName", val)
                  }
                  onBlur={formik.handleBlur}
                  {...getFieldErrorProps(formik, "activityName")}
                />
              </EditableDetailRow>
            </Grid>

            {/* Assigned To */}
            <Grid size={12}>
              <EditableDetailRow label="Assigned To">
                <MuiAutocomplete
                  {...commonAutoCompleteProps}
                  name="assignedTo"
                  options={projectUsersOptions}
                  disabled={disabled}
                  getOptionLabel={(option) =>
                    typeof option === "string"
                      ? option
                      : option?.userName ||
                      option?.email ||
                      option?.firstName ||
                      ""
                  }
                  value={formik.values.assignedTo}
                  onChange={(e, val) => formik.setFieldValue("assignedTo", val)}
                  onBlur={formik.handleBlur}
                  {...getFieldErrorProps(formik, "assignedTo")}
                />
              </EditableDetailRow>
            </Grid>


            {/* Due Date */}
            <Grid size={12}>
              <EditableDetailRow label="Due Date" required>
                <DateTimePicker
                  format="DD/MM/YYYY hh:mm A"
                  value={parseDateTimeValue(formik.values.dueDate)}
                  onChange={(val) => formik.setFieldValue("dueDate", val)}
                  disabled={disabled}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      onBlur: () => formik.setFieldTouched("dueDate", true),
                      ...getFieldErrorProps(formik, "dueDate"),
                      sx: {
                        "& .MuiInputBase-input": {
                          fontSize: "0.8rem",
                          py: 0.7,
                        },
                      },
                    },
                  }}
                />
              </EditableDetailRow>
            </Grid>

            {/* Actual Date */}
            <Grid size={12}>
              <EditableDetailRow label="Actual Date">
                <DateTimePicker
                  format="DD/MM/YYYY hh:mm A"
                  value={parseDateTimeValue(formik.values.actualDate)}
                  onChange={(val) => formik.setFieldValue("actualDate", val)}
                  disabled={disabled}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      onBlur: () => formik.setFieldTouched("actualDate", true),
                      ...getFieldErrorProps(formik, "actualDate"),
                      sx: {
                        "& .MuiInputBase-input": {
                          fontSize: "0.8rem",
                          py: 0.7,
                        },
                      },
                    },
                  }}
                />
              </EditableDetailRow>
            </Grid>

            {/* Priority */}
            <Grid size={12}>
              <EditableDetailRow label="Priority" required>
                <MuiAutocomplete
                  {...commonAutoCompleteProps}
                  name="priority"
                  options={crmPriorityOptions}
                  disabled={disabled}
                  getOptionLabel={(option) =>
                    typeof option === "string"
                      ? option
                      : option?.priorityType || option?.name || ""
                  }
                  value={formik.values.priority || null}
                  onChange={(e, val) => formik.setFieldValue("priority", val)}
                  onBlur={formik.handleBlur}
                  {...getFieldErrorProps(formik, "priority")}
                />
              </EditableDetailRow>
            </Grid>

          </Grid>
        </Grid>

        {/* RIGHT COLUMN */}
        <Grid size={{ xs: 12, lg: 12 }}>
          <Grid container spacing={1}>




            {/* Status */}
            <Grid size={12}>
              <EditableDetailRow label="Status">
                <MuiAutocomplete
                  {...commonAutoCompleteProps}
                  name="status"
                  options={statusOptions}
                  disabled={disabled}
                  getOptionLabel={(option) =>
                    typeof option === "string"
                      ? option
                      : option?.activityStatus || option?.status || option?.name || ""
                  }
                  isOptionEqualToValue={(option, value) => {
                    if (!value) return false;
                    if (typeof value === "string") {
                      return (
                        option?.activityStatus === value ||
                        option?.status === value ||
                        option?.name === value
                      );
                    }
                    return (
                      (option?.id && option?.id === value?.id) ||
                      (option?.activityStatus &&
                        option?.activityStatus ===
                        (value?.activityStatus || value?.status || value?.name)) ||
                      (option?.status &&
                        option?.status ===
                        (value?.activityStatus || value?.status || value?.name))
                    );
                  }}
                  value={formik.values.status}
                  onChange={(e, val) => formik.setFieldValue("status", val)}
                  onBlur={formik.handleBlur}
                  {...getFieldErrorProps(formik, "status")}
                />
              </EditableDetailRow>
            </Grid>

            <Grid size={12}>
              <EditableDetailRow label="Description">
                <MyTextInput
                  type="text"
                  multiline
                  minRows={2}
                  maxRows={5}
                  name="description"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={disabled}
                  {...commonInputProps}
                  {...getFieldErrorProps(formik, "description")}
                />
              </EditableDetailRow>
            </Grid>

            <Grid size={12}>
              <EditableDetailRow label="Outcome Note">
                <MyTextInput
                  type="text"
                  multiline
                  minRows={2}
                  maxRows={5}
                  name="outComeNote"
                  value={formik.values.outComeNote}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={disabled}
                  {...commonInputProps}
                  {...getFieldErrorProps(formik, "outComeNote")}
                />
              </EditableDetailRow>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </LocalizationProvider>
  );
};

export default ActivityEdit;
